/* istanbul ignore file */
/* eslint-disable react/prop-types */
import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Divider, FormControl, IconButton, InputLabel, List, ListItem, Typography } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';

import { ArrowLink, InputField, Select, Spinner } from '../../../../components';

const Label = ({ label }) => (
  <Box width="170px">
    <Typography variant="bold">{label}</Typography>
  </Box>
);

const ElementWrapper = ({ children, label }) => (
  <Box display="flex" gap={1} alignItems="center">
    <Label label={label} />
    {children}
  </Box>
);

const ComponentLibrary = () => {
  return (
    <div>
      <Typography variant="h1" sx={{ marginBottom: 2 }}>
        Smartcar Dashboard Component Library
      </Typography>
      <Typography>
        Design patterns and examples of how to implement various elements across our dashboard.
      </Typography>

      <List sx={{ marginY: 3 }}>
        <ListItem>
          <a href="#typography">Typography</a>
        </ListItem>
        <ListItem>
          <a href="#buttons">Buttons</a>
        </ListItem>
        <ListItem>
          <a href="#inputs">Inputs</a>
        </ListItem>
        <ListItem>
          <a href="#selects">Select dropdowns</a>
        </ListItem>
      </List>

      <Divider sx={{ marginY: 5 }} />

      <Typography variant="h1" id="typography">Typography</Typography>
      <Box display="flex" flexDirection="column" gap={2} mt={3} mb={3}>
        <ElementWrapper label=" H1">
          <Typography variant="h1">Heading 1</Typography>
        </ElementWrapper>
        <ElementWrapper label=" H2">
          <Typography variant="h2">Heading 2</Typography>
        </ElementWrapper>
        <ElementWrapper label=" H3">
          <Typography variant="h3">Heading 3</Typography>
        </ElementWrapper>
        <ElementWrapper label="body1">
          <Typography variant="body1">This is our primary text and font size.</Typography>
        </ElementWrapper>
        <ElementWrapper label="bold">
          <Typography variant="bold">This is our primary text in bold.</Typography>
        </ElementWrapper>
        <ElementWrapper label="body2">
          <Typography variant="body2">This is our secondary text and font size.</Typography>
        </ElementWrapper>
        <ElementWrapper label="caption">
          <Typography variant="caption">This is our caption text and font size. It uses our secondary text color.</Typography>
        </ElementWrapper>
      </Box>

      <Divider sx={{ marginY: 5 }} />

      <Typography variant="h1" id="buttons">Buttons & Links</Typography>
      <Box display="flex" flexDirection="column" gap={2} mt={3} mb={3}>
        <Typography>
          Hover over and tab through the buttons to view hover and focused states.
        </Typography>

        <ElementWrapper label="Primary">
          <Button variant="outlined">Primary</Button>
          <Button variant="outlined" disabled>Primary disabled</Button>
        </ElementWrapper>

        <ElementWrapper label="Secondary">
          <Button variant="outlined" color="secondary">Secondary</Button>
          <Button variant="outlined" disabled color="secondary">Secondary disabled</Button>
        </ElementWrapper>

        <ElementWrapper label="Tertiary (Text)">
          <Button variant="text">Text primary</Button>
          <Button variant="text" disabled>Text disabled</Button>
        </ElementWrapper>

        <ElementWrapper label="Small font">
          <Button variant="outlined" size="small">Primary</Button>
          <Button variant="outlined" disabled size="small">Primary disabled</Button>
        </ElementWrapper>

        <ElementWrapper label="Error">
          <Button variant="outlined" color="error">Error</Button>
          <Button variant="outlined" disabled color="error">Error disabled</Button>
        </ElementWrapper>

        <ElementWrapper label="Upgrade">
          <Button variant="outlined" color="warning">Upgrade</Button>
          <Button variant="outlined" disabled color="warning">Upgrade disabled</Button>
        </ElementWrapper>

        <ElementWrapper label="Contained (unused)">
          <Button variant="contained">Contained</Button>
          <Button variant="contained" disabled>Contained disabled</Button>
        </ElementWrapper>

        <ElementWrapper label="With start icon">
          <Button variant="outlined" startIcon={<AddRoundedIcon />}>Button</Button>
        </ElementWrapper>

        <ElementWrapper label="With end icon">
          <Button variant="outlined" color="secondary" endIcon={<AddRoundedIcon />}>Button</Button>
        </ElementWrapper>

        <Box display="flex" gap={1} alignItems="center">
          <Label label="Loading button" />
          <Button variant="outlined" startIcon={<Spinner size="button" />} disabled>
            Loading
          </Button>
        </Box>

        <ElementWrapper label="Icon only">
          <IconButton aria-label="copy">
            <ContentCopyRoundedIcon />
          </IconButton>
        </ElementWrapper>

        <Box display="flex" gap={1} alignItems="center">
          <Label label="Icon (no border)" />
          <IconButton aria-label="close" sx={{ border: 'none' }}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        <Box display="flex" gap={1} alignItems="center">
          <Label label="Unstyled" />
          <Button variant="unstyled">Unstyled button</Button>
        </Box>

        <Box display="flex" gap={1} alignItems="center">
          <Label label="Internal link" />
          <Link to="/">Internal link</Link>
        </Box>

        <Box display="flex" gap={1} alignItems="center">
          <Label label="External link" />
          <ArrowLink to="https://smartcar.com" text="External link" />
        </Box>

        <Box display="flex" gap={1} alignItems="center">
          <Label label="Button link" />
          <Link to="/" className="button-link">Button link</Link>
        </Box>

        <Box display="flex" gap={1} alignItems="center">
          <Label label="Button link (upgrade)" />
          <Link to="/" className="button-link button-link--warning">Button link</Link>
        </Box>
      </Box>

      <Divider sx={{ marginY: 5 }} />

      <Typography variant="h1" sx={{ marginTop: 3 }} id="inputs">
        Inputs
      </Typography>
      <Box display="flex" flexDirection="column" gap={3} mt={3} mb={3}>
        <ElementWrapper label="Default input">
          <InputField
            id="text-input"
            label="Default input"
            inputPlaceholder="Enter text here"
            handleChange={() => {}}
            inputValues=""
          />
        </ElementWrapper>
        <ElementWrapper label="Read only">
          <InputField
            id="text-input"
            label="Read only input"
            inputPlaceholder="Enter text here"
            handleChange={() => {}}
            inputValues="1234-5678-9012-3456"
            inputReadOnly
          />
        </ElementWrapper>
        <ElementWrapper label="Disabled">
          <InputField
            id="text-input"
            label="Disabled input"
            inputPlaceholder="Enter text here"
            handleChange={() => {}}
            inputValues="dashboard@smartcar.com"
            inputDisabled
          />
        </ElementWrapper>
        <ElementWrapper label="With no label">
          <InputField
            id="text-input"
            inputPlaceholder="Enter text here"
            handleChange={() => {}}
            inputValues=""
          />
        </ElementWrapper>
        <ElementWrapper label="With description">
          <InputField
            id="text-input"
            label="Label"
            description="This is a description for the input field."
            inputPlaceholder="Enter text here"
            handleChange={() => {}}
            inputValues=""
          />
        </ElementWrapper>
        <ElementWrapper label="With error message">
          <InputField
            id="text-input"
            label="Invalid input"
            inputPlaceholder="Enter text here"
            handleChange={() => {}}
            inputValues=""
            errorMessage="This is an error message for the input"
          />
        </ElementWrapper>
        <ElementWrapper label="With actions">
          <InputField
            id="text-input"
            label="Invalid input"
            inputPlaceholder="Enter text here"
            handleChange={() => {}}
            inputValues=""
            actions={(
              <Box display="flex" gap={1}>
                <IconButton><ContentCopyRoundedIcon /></IconButton>
                <IconButton><DeleteOutlineRoundedIcon /></IconButton>
              </Box>
            )}
          />
        </ElementWrapper>
        <ElementWrapper label="With hover actions">
          <InputField
            id="text-input"
            label="Invalid input"
            inputPlaceholder="Enter text here"
            handleChange={() => {}}
            inputValues=""
            showActionsOnHover
            actions={(
              <IconButton><ContentCopyRoundedIcon /></IconButton>
            )}
          />
        </ElementWrapper>
        <ElementWrapper label="Custom width (default is 360px)">
          <InputField
            id="text-input"
            label="Custom width"
            inputPlaceholder="Enter text here"
            handleChange={() => {}}
            inputValues=""
            customInputWidth="160px"
          />
        </ElementWrapper>
        <ElementWrapper label="Full width">
          <Box width="700px">
            <InputField
              id="text-input"
              label="Full width (container is 700px)"
              inputPlaceholder="Enter text here"
              handleChange={() => {}}
              inputValues=""
              fullWidth
            />
          </Box>
        </ElementWrapper>
      </Box>

      <Divider sx={{ marginY: 5 }} />

      <Typography variant="h1" id="selects" sx={{ marginTop: 3 }}>
        Select dropdowns
      </Typography>
      <Box display="flex" flexDirection="column" gap={4} mt={3} mb={6}>
        <ElementWrapper label="Default select">
          <Select
            id="default-select"
            label="Default select"
            handleChange={() => {}}
            options={[{ value: 'acura', label: 'Acura' }, { value: 'audi', label: 'Audi' }]}
            value="acura"
            selectStyles={{ width: '200px' }}
          />
        </ElementWrapper>
        <ElementWrapper label="With placeholder">
          <Select
            id="default-select"
            label="Default select"
            handleChange={() => {}}
            options={[{ value: 'acura', label: 'Acura' }, { value: 'audi', label: 'Audi' }]}
            value=""
            placeholderOptionLabel="Select a make"
            selectStyles={{ width: '200px' }}
          />
        </ElementWrapper>
        <ElementWrapper label="Full width">
          <Box width="700px">
            <Select
              id="default-select"
              label="Default select"
              handleChange={() => {}}
              options={[{ value: 'acura', label: 'Acura' }, { value: 'audi', label: 'Audi' }]}
              value=""
              placeholderOptionLabel="Select a model"
            />
          </Box>
        </ElementWrapper>
        <ElementWrapper label="With label">
          <FormControl>
            <InputLabel id="select-label">Select with label</InputLabel>
            <Select
              id="default-select"
              labelId="select-label"
              handleChange={() => {}}
              options={[{ value: 'acura', label: 'Acura' }, { value: 'audi', label: 'Audi' }]}
              value=""
              placeholderOptionLabel="Select a model"
              selectStyles={{ width: '200px' }}
            />
          </FormControl>
        </ElementWrapper>
      </Box>
    </div>
  );
};

export default ComponentLibrary;
